import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { Badge, Button, Drawer, LoadingSkeleton } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';

/* ── Contratos reais (backend/.review-loop/reports/api-contracts.md §6–7) ──
 * GET /inventory/balance              → StockBalanceResponseDto[] (array puro; filtros
 *                                       warehouseId/productId/lotNumber/serialNumber; SEM paginação)
 * GET /inventory/movements            → {data,total,page,limit} (filtros warehouseId/productId/…)
 * GET /inventory/warehouses           → Warehouse[]
 * GET /products?tenantId=             → ProductResponseDto[] (array, sem paginação servidor)
 * GET /categories                     → Category[]
 * NÃO EXISTE GET /tenants/{id}/inventory/stock nem envelope {data,total} no balance.
 */

interface StockBalance {
  id: string;
  tenantId: string;
  companyId: string;
  warehouseId: string;
  productId: string;
  onHand: number;
  reserved: number;
  available: number;
  lotNumber: string | null;
  serialNumber: string | null;
  expirationDate: string | null;
  unitCost: number;
  version: number;
}

interface ProductLite {
  id: string;
  name: string;
  sku: string;
  unit: string;
  status: string;
  categoryId?: string;
}

interface Category {
  id: string;
  name: string;
}

interface Warehouse {
  id: string;
  name: string;
  code: string;
  active: boolean;
}

interface Movement {
  id: string;
  productId: string;
  quantity: number;
  type: string;
  warehouseId: string;
  createdAt: string;
  reference?: string | null;
}

interface Paged<T> {
  data: T[];
  total: number;
}

/** Limite fixo client-side para o pill "Baixo": o contrato do balance não expõe
 *  estoque mínimo, então a faixa é uma projeção explícita (ver A7 no evidence). */
const LOW_STOCK_LIMIT = 5;

type StockStatus = 'OUT' | 'LOW' | 'OK';

function stockStatus(available: number): StockStatus {
  if (available <= 0) return 'OUT';
  if (available <= LOW_STOCK_LIMIT) return 'LOW';
  return 'OK';
}

const STATUS_PILL: Record<StockStatus, { variant: 'error' | 'warning' | 'success'; label: string }> = {
  OUT: { variant: 'error', label: 'Sem estoque' },
  LOW: { variant: 'warning', label: `Baixo (projeção ≤ ${LOW_STOCK_LIMIT} un.)` },
  OK: { variant: 'success', label: 'OK' },
};

const PAGE_SIZE = 20;

/** Cabecalhos da tabela de inventario; `right` marca coluna numerica (mono). */
const HEADERS: ReadonlyArray<readonly [string, 'left' | 'right']> = [
  ['Produto', 'left'],
  ['Armazém', 'left'],
  ['Lote / Série', 'left'],
  ['Em mãos', 'right'],
  ['Reservado', 'right'],
  ['Disponível', 'right'],
  ['Status', 'left'],
  ['Custo médio', 'right'],
];

export function StockPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const balanceQ = useQuery<StockBalance[]>({
    queryKey: [...queryKeys.inventory.stock(tenantId), { warehouseId }],
    queryFn: async () =>
      apiClient.get<StockBalance[]>('/inventory/balance', {
        params: warehouseId ? { warehouseId } : {},
      }),
    enabled: !!tenantId,
  });
  const productsQ = useQuery<ProductLite[]>({
    queryKey: queryKeys.masterData.products(tenantId),
    queryFn: async () => apiClient.get<ProductLite[]>('/products', { params: { tenantId } }),
    enabled: !!tenantId,
  });
  const categoriesQ = useQuery<Category[]>({
    queryKey: queryKeys.masterData.categories(),
    queryFn: async () => apiClient.get<Category[]>('/categories'),
    enabled: !!tenantId,
  });
  const warehousesQ = useQuery<Warehouse[]>({
    queryKey: queryKeys.inventory.warehouses(),
    /* Backend devolve envelope paginado. Sem extrair `.data`, o mapa de
       armazem fica vazio e o filtro "Todos os armazens" nao lista nada. */
    queryFn: async () =>
      (await apiClient.get<{ data: Warehouse[] }>('/inventory/warehouses')).data,
    enabled: !!tenantId,
  });

  const productMap = useMemo(() => {
    const map = new Map<string, ProductLite>();
    for (const p of productsQ.data ?? []) map.set(p.id, p);
    return map;
  }, [productsQ.data]);
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categoriesQ.data ?? []) map.set(c.id, c.name);
    return map;
  }, [categoriesQ.data]);
  const warehouseMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const w of warehousesQ.data ?? []) map.set(w.id, w.name);
    return map;
  }, [warehousesQ.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (balanceQ.data ?? []).filter((b) => {
      const p = productMap.get(b.productId);
      if (categoryId && p?.categoryId !== categoryId) return false;
      if (!q) return true;
      const hay = `${p?.name ?? ''} ${p?.sku ?? ''} ${b.lotNumber ?? ''} ${b.serialNumber ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [balanceQ.data, productMap, categoryId, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const from = filtered.length === 0 ? 0 : safePage * PAGE_SIZE + 1;
  const to = Math.min(filtered.length, safePage * PAGE_SIZE + PAGE_SIZE);

  const kpis = useMemo(() => {
    const items = balanceQ.data ?? [];
    const units = items.reduce((a, b) => a + b.onHand, 0);
    const value = items.reduce((a, b) => a + b.onHand * b.unitCost, 0);
    const out = items.filter((b) => stockStatus(b.available) === 'OUT').length;
    const low = items.filter((b) => stockStatus(b.available) === 'LOW').length;
    return { skus: items.length, units, value, out, low };
  }, [balanceQ.data]);

  const selected = useMemo(
    () => (balanceQ.data ?? []).find((b) => b.id === selectedId) ?? null,
    [balanceQ.data, selectedId],
  );

  const historyQ = useQuery<Paged<Movement>>({
    queryKey: [...queryKeys.inventory.movements(), { productId: selected?.productId ?? '' }],
    queryFn: async () =>
      apiClient.get<Paged<Movement>>('/inventory/movements', {
        params: { productId: selected?.productId ?? '', page: '1', limit: '10' },
      }),
    enabled: !!selected,
  });

  useEffect(() => {
    if (selected) closeRef.current?.focus();
  }, [selected]);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setSelectedId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const isLoading = balanceQ.isLoading || productsQ.isLoading;
  const loadError = balanceQ.error ?? productsQ.error;

  return (
    /* ───────────────────────────────────────────────────────────────────────
       Portado de base-html, slug `erpone-gest-o-de-estoque`. A composição é a
       do protótipo: cabeçalho display-lg, 4 KPIs em bento grid, card de tabela
       com toolbar e drawer contextual.

       Todo número exibido vem do `balance` real. Os KPIs 3 e 4 do protótipo
       ("Pedidos em Trânsito" e "Giro de Estoque") NÃO têm fonte no backend —
       foram trocados por SKUs com Saldo e Sem Estoque, que são reais, em vez de
       exibir valor inventado com cara de métrica.
       ─────────────────────────────────────────────────────────────────────── */
    /* <main> do prototipo virou <div>: o shell ja tem o landmark main,
       e dois <main> na mesma pagina quebram a navegacao por landmark. */
    <div className="flex-1 flex flex-col h-full w-full relative">
      <div className="flex-1 overflow-y-auto p-gutter bg-background relative">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-lg gap-md">
          <div>
            <h1 className="font-display-lg text-display-lg text-on-surface mb-xs">Gestão de Estoque</h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Visão geral do inventário e controle de reposição.
            </p>
          </div>
          <div className="flex gap-sm w-full sm:w-auto">
            <button
              type="button"
              onClick={() => void navigate({ to: '/inventory/movements' })}
              className="flex-1 sm:flex-none bg-surface text-primary border border-outline-variant hover:bg-surface-container-high px-md py-sm rounded font-label-md text-label-md transition-colors shadow-sm flex items-center justify-center gap-xs"
            >
              <span className="material-symbols-outlined text-[18px]">build</span>
              Ajuste de Estoque
            </button>
            <button
              type="button"
              onClick={() => void navigate({ to: '/procurement/orders' })}
              className="flex-1 sm:flex-none bg-primary text-on-primary hover:opacity-90 px-md py-sm rounded font-label-md text-label-md transition-opacity shadow-sm flex items-center justify-center gap-xs"
            >
              <span className="material-symbols-outlined text-[18px]">shopping_cart_checkout</span>
              Novo Pedido
            </button>
          </div>
        </div>

        {loadError ? (
          <div
            role="alert"
            className="bg-error-container text-on-error-container border border-error/30 rounded-xl p-md mb-lg"
          >
            <p className="font-body-md text-body-md font-semibold">Erro ao carregar o saldo de estoque.</p>
            <p className="font-body-sm text-body-sm mb-sm">{(loadError as Error).message}</p>
            <button
              type="button"
              onClick={() => void balanceQ.refetch()}
              className="bg-primary text-on-primary px-md py-sm rounded font-label-md text-label-md"
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-md mb-xl" aria-live="polite">
              <div className="bg-surface rounded-xl p-md border border-outline-variant/50 shadow-sm relative overflow-hidden group hover:border-primary/30 transition-colors">
                <div className="absolute top-0 right-0 w-24 h-24 bg-primary/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                <div className="flex justify-between items-start mb-sm relative z-10">
                  <span className="font-label-md text-label-md text-on-surface-variant uppercase">
                    Valor Total em Estoque
                  </span>
                  <div className="w-8 h-8 rounded-full bg-primary-container/20 flex items-center justify-center text-primary-container">
                    <span className="material-symbols-outlined text-[20px]">account_balance_wallet</span>
                  </div>
                </div>
                <div className="relative z-10">
                  <span className="font-headline-md text-headline-md text-primary block">
                    {isLoading ? '—' : formatCurrency(kpis.value)}
                  </span>
                  <div className="flex items-center mt-sm gap-xs text-secondary">
                    <span className="font-body-sm text-body-sm">Σ onHand × custo médio</span>
                  </div>
                </div>
              </div>

              <div className="bg-surface rounded-xl p-md border border-error/20 shadow-sm relative overflow-hidden group hover:border-error/40 transition-colors">
                <div className="absolute top-0 right-0 w-24 h-24 bg-error/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                <div className="flex justify-between items-start mb-sm relative z-10">
                  <span className="font-label-md text-label-md text-on-surface-variant uppercase">Estoque Baixo</span>
                  <div className="w-8 h-8 rounded-full bg-error/10 flex items-center justify-center text-error">
                    <span className="material-symbols-outlined text-[20px]">warning</span>
                  </div>
                </div>
                <div className="relative z-10">
                  <span className="font-headline-md text-headline-md text-error block">
                    {isLoading ? '—' : kpis.low}
                  </span>
                  <div className="flex items-center mt-sm gap-xs text-error">
                    <span
                      className="font-body-sm text-body-sm font-semibold"
                      title={`Projeção no cliente: 0 < available <= ${LOW_STOCK_LIMIT}. O contrato do balance não expõe estoque mínimo.`}
                    >
                      Projeção — sem mínimo no contrato
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-surface rounded-xl p-md border border-outline-variant/50 shadow-sm relative overflow-hidden group hover:border-primary/30 transition-colors">
                <div className="absolute top-0 right-0 w-24 h-24 bg-secondary/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                <div className="flex justify-between items-start mb-sm relative z-10">
                  <span className="font-label-md text-label-md text-on-surface-variant uppercase">SKUs com Saldo</span>
                  <div className="w-8 h-8 rounded-full bg-secondary-container/50 flex items-center justify-center text-on-secondary-container">
                    <span className="material-symbols-outlined text-[20px]">inventory_2</span>
                  </div>
                </div>
                <div className="relative z-10">
                  <span className="font-headline-md text-headline-md text-primary block">
                    {isLoading ? '—' : kpis.skus}
                  </span>
                  <div className="flex items-center mt-sm gap-xs text-secondary">
                    <span className="font-body-sm text-body-sm">
                      {isLoading ? '' : `${kpis.units.toLocaleString('pt-BR')} un. em mãos`}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-surface rounded-xl p-md border border-outline-variant/50 shadow-sm relative overflow-hidden group hover:border-primary/30 transition-colors">
                <div className="absolute top-0 right-0 w-24 h-24 bg-tertiary/5 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                <div className="flex justify-between items-start mb-sm relative z-10">
                  <span className="font-label-md text-label-md text-on-surface-variant uppercase">Sem Estoque</span>
                  <div className="w-8 h-8 rounded-full bg-tertiary-container/10 flex items-center justify-center text-tertiary-container">
                    <span className="material-symbols-outlined text-[20px]">production_quantity_limits</span>
                  </div>
                </div>
                <div className="relative z-10">
                  <span className="font-headline-md text-headline-md text-primary block">
                    {isLoading ? '—' : kpis.out}
                  </span>
                  <div className="flex items-center mt-sm gap-xs text-secondary">
                    <span className="font-body-sm text-body-sm">available menor ou igual a zero</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-xl border border-outline-variant shadow-sm flex flex-col overflow-hidden">
              <div className="p-md border-b border-outline-variant bg-surface-container-lowest flex flex-col sm:flex-row justify-between items-center gap-sm">
                <h3 className="font-headline-sm text-headline-sm text-primary">Inventário Principal</h3>
                <div className="flex flex-wrap items-center gap-sm w-full sm:w-auto">
                  <div className="relative">
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                      }}
                      placeholder="Buscar produto, SKU, lote ou série…"
                      className="bg-surface-container-low border border-outline-variant font-body-sm text-body-sm rounded py-2 pl-xl pr-sm focus:ring-1 focus:ring-primary focus:border-primary w-full sm:w-64"
                    />
                    <span className="material-symbols-outlined absolute left-sm top-1/2 -translate-y-1/2 text-outline pointer-events-none text-[18px]">
                      search
                    </span>
                  </div>
                  <select
                    aria-label="Armazém"
                    value={warehouseId}
                    onChange={(e) => {
                      setWarehouseId(e.target.value);
                      setPage(0);
                    }}
                    className="bg-surface-container-low border border-outline-variant font-body-sm text-body-sm rounded py-2 px-sm focus:ring-1 focus:ring-primary focus:border-primary cursor-pointer"
                  >
                    <option value="">Todos os armazéns</option>
                    {(warehousesQ.data ?? []).map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Categoria"
                    value={categoryId}
                    onChange={(e) => {
                      setCategoryId(e.target.value);
                      setPage(0);
                    }}
                    className="bg-surface-container-low border border-outline-variant font-body-sm text-body-sm rounded py-2 px-sm focus:ring-1 focus:ring-primary focus:border-primary cursor-pointer"
                  >
                    <option value="">Todas as categorias</option>
                    {(categoriesQ.data ?? []).map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                {isLoading ? (
                  <div className="p-lg" aria-busy="true" aria-live="polite">
                    <LoadingSkeleton width="100%" height="220px" />
                  </div>
                ) : pageItems.length === 0 ? (
                  <p className="p-xl text-center font-body-md text-body-md text-on-surface-variant" role="status">
                    Nenhum saldo encontrado
                    {search || categoryId || warehouseId ? ' para estes filtros' : ''}.
                  </p>
                ) : (
                  <table className="w-full border-collapse font-body-sm text-body-sm">
                    <thead className="sticky top-0 z-[1]">
                      <tr className="bg-surface-container-low">
                        {HEADERS.map(([label, align]) => (
                          <th
                            key={label}
                            scope="col"
                            className={`px-md py-sm font-label-md text-label-md uppercase text-on-surface-variant whitespace-nowrap border-b-2 border-outline-variant ${
                              align === 'right' ? 'text-right' : 'text-left'
                            }`}
                          >
                            {label}
                          </th>
                        ))}
                        <th scope="col" className="px-md py-sm border-b-2 border-outline-variant">
                          <span className="sr-only">Ações</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.map((b) => {
                        const prod = productMap.get(b.productId);
                        const st = stockStatus(b.available);
                        const pill = STATUS_PILL[st];
                        return (
                          <tr key={b.id} className="hover:bg-surface-container-low transition-colors">
                            <td className="px-md py-sm border-b border-outline-variant/60">
                              <span className="block font-semibold text-on-surface">
                                {prod?.name ?? b.productId.slice(0, 8)}
                              </span>
                              <span className="block font-label-sm text-label-sm text-on-surface-variant">
                                {prod?.sku ?? 'SKU indisponível'} ·{' '}
                                {prod?.categoryId ? (categoryMap.get(prod.categoryId) ?? '—') : 'Sem categoria'}
                              </span>
                            </td>
                            <td className="px-md py-sm border-b border-outline-variant/60 text-on-surface-variant">
                              {warehouseMap.get(b.warehouseId) ?? b.warehouseId.slice(0, 8)}
                            </td>
                            <td className="px-md py-sm border-b border-outline-variant/60 font-label-md text-label-md text-on-surface-variant">
                              {b.lotNumber ?? b.serialNumber ?? '—'}
                            </td>
                            <td className="px-md py-sm border-b border-outline-variant/60 text-right font-label-md text-label-md tabular-nums">
                              {b.onHand}
                            </td>
                            <td className="px-md py-sm border-b border-outline-variant/60 text-right font-label-md text-label-md tabular-nums">
                              {b.reserved}
                            </td>
                            <td className="px-md py-sm border-b border-outline-variant/60 text-right font-label-md text-label-md tabular-nums font-bold">
                              {b.available}
                            </td>
                            <td className="px-md py-sm border-b border-outline-variant/60">
                              <span
                                title={
                                  st === 'LOW'
                                    ? 'Projeção no cliente: o contrato do balance não expõe estoque mínimo.'
                                    : 'Regra única sobre available do balance'
                                }
                              >
                                <Badge variant={pill.variant}>{pill.label}</Badge>
                              </span>
                            </td>
                            <td className="px-md py-sm border-b border-outline-variant/60 text-right font-label-md text-label-md tabular-nums">
                              {formatCurrency(b.unitCost)}
                            </td>
                            <td className="px-md py-sm border-b border-outline-variant/60 text-right">
                              <button
                                type="button"
                                onClick={() => setSelectedId(b.id)}
                                className="text-primary font-label-md text-label-md hover:underline"
                              >
                                Detalhes
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="p-md border-t border-outline-variant bg-surface-container-lowest flex flex-col sm:flex-row justify-between items-center gap-sm">
                <span className="font-body-sm text-body-sm text-on-surface-variant" aria-live="polite">
                  Mostrando {from}–{to} de {filtered.length}
                </span>
                <span className="flex items-center gap-xs">
                  <button
                    type="button"
                    disabled={safePage === 0}
                    onClick={() => setPage(safePage - 1)}
                    aria-label="Página anterior"
                    className="w-8 h-8 rounded border border-outline-variant text-on-surface-variant disabled:opacity-40 hover:bg-surface-container"
                  >
                    ‹
                  </button>
                  <span className="font-label-md text-label-md text-on-surface-variant px-xs">
                    {safePage + 1} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={safePage >= totalPages - 1}
                    onClick={() => setPage(safePage + 1)}
                    aria-label="Próxima página"
                    className="w-8 h-8 rounded border border-outline-variant text-on-surface-variant disabled:opacity-40 hover:bg-surface-container"
                  >
                    ›
                  </button>
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelectedId(null)}
        title={selected ? (productMap.get(selected.productId)?.name ?? 'Item de estoque') : ''}
        description={selected ? (productMap.get(selected.productId)?.sku ?? undefined) : undefined}
        footer={
          <>
            <Button variant="secondary" onClick={() => void navigate({ to: '/inventory/movements' })}>
              Ver movimentações
            </Button>
            <Button variant="secondary" onClick={() => void navigate({ to: '/master-data/products' })}>
              Ver produtos
            </Button>
          </>
        }
      >
        {selected ? (
          <>
            <dl className="grid grid-cols-2 gap-md mb-lg">
              {[
                ['Armazém', warehouseMap.get(selected.warehouseId) ?? selected.warehouseId],
                ['Em mãos', String(selected.onHand)],
                ['Reservado', String(selected.reserved)],
                ['Disponível', String(selected.available)],
                ['Lote', selected.lotNumber ?? '—'],
                ['Série', selected.serialNumber ?? '—'],
                ['Validade', formatDate(selected.expirationDate)],
                ['Custo médio', formatCurrency(selected.unitCost)],
                ['Valor total', formatCurrency(selected.onHand * selected.unitCost)],
              ].map(([label, value]) => (
                <div key={label}>
                  <dt className="font-label-sm text-label-sm text-on-surface-variant uppercase">{label}</dt>
                  <dd className="font-body-md text-body-md text-on-surface m-0">{value}</dd>
                </div>
              ))}
            </dl>

            <h3 className="font-headline-sm text-headline-sm text-primary mb-sm">Histórico recente</h3>
            {historyQ.isLoading ? (
              <LoadingSkeleton width="100%" height="120px" />
            ) : historyQ.error ? (
              <p className="font-body-sm text-body-sm text-error" role="alert">
                Histórico indisponível: {(historyQ.error as Error).message}
              </p>
            ) : (historyQ.data?.data ?? []).length === 0 ? (
              <p className="font-body-sm text-body-sm text-on-surface-variant" role="status">
                Sem movimentações para este produto.
              </p>
            ) : (
              <ul className="list-none m-0 p-0 flex flex-col gap-sm">
                {(historyQ.data?.data ?? []).map((mv) => (
                  <li
                    key={mv.id}
                    className="border-b border-outline-variant/60 pb-sm font-body-sm text-body-sm text-on-surface"
                  >
                    <strong>{mv.type}</strong> · {mv.quantity} un. · {formatDate(mv.createdAt)}
                    {mv.reference ? (
                      <span className="block font-label-sm text-label-sm text-on-surface-variant">
                        ref. {mv.reference}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : null}
      </Drawer>
    </div>
  );
}
